import { List, ListItem, SimpleGrid, ThemeIcon, Tooltip } from '@mantine/core'
import { IconCheck, IconX } from '@tabler/icons-react'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { LicenseProduct } from '@/types/subscription'
import { UNLIMITED_USAGE } from '../usageConfig'

// -1 is used to indicate unlimited usage in translations
const getFormattedValue = (value: number) => {
  return value === UNLIMITED_USAGE ? -1 : value
}

type FeatureListItemProps = {
  label: ReactNode
  tooltip?: ReactNode
  isIncluded?: boolean
}

const FeatureListItem = ({
  label,
  tooltip,
  isIncluded
}: FeatureListItemProps) => {
  return (
    <ListItem
      icon={
        <ThemeIcon
          variant="transparent"
          color={isIncluded ? 'green' : 'red'}
          size="md"
          radius="xl"
        >
          {isIncluded ? (
            <IconCheck style={{ width: '80%', height: '80%' }} stroke={3} />
          ) : (
            <IconX style={{ width: '80%', height: '80%' }} stroke={3} />
          )}
        </ThemeIcon>
      }
    >
      <Tooltip disabled={!tooltip} label={tooltip}>
        <span>{label}</span>
      </Tooltip>
    </ListItem>
  )
}

type LicenseFeaturesProps = {
  license: LicenseProduct
}

export const LicenseFeatures = ({ license }: LicenseFeaturesProps) => {
  return (
    <SimpleGrid cols={2}>
      <List size="sm">
        <FeatureListItem
          label={
            <FormattedMessage
              id="subscriptions.features.cameraStreams"
              values={{
                count: getFormattedValue(license.number_of_streams)
              }}
            />
          }
          isIncluded
        />

        <FeatureListItem
          label={
            <FormattedMessage
              id="subscriptions.features.users"
              values={{ count: getFormattedValue(license.number_of_users) }}
            />
          }
          isIncluded
        />

        <FeatureListItem
          label={
            <FormattedMessage
              id="subscriptions.features.applications"
              values={{ count: getFormattedValue(license.number_of_apps) }}
            />
          }
          isIncluded
        />

        <FeatureListItem
          label={
            <FormattedMessage
              id="subscriptions.features.modelTrainings"
              values={{
                count: getFormattedValue(license.model_trainings_per_month)
              }}
            />
          }
          isIncluded
        />

        <FeatureListItem
          label={
            <FormattedMessage
              id="subscriptions.features.trainedModels"
              values={{
                count: getFormattedValue(license.version_control_models)
              }}
            />
          }
          isIncluded
        />

        <FeatureListItem
          label={
            <FormattedMessage
              id="subscriptions.features.testModelDataSize"
              values={{
                size: getFormattedValue(license.model_test_data_per_month_gb)
              }}
            />
          }
          isIncluded
        />
      </List>

      <List size="sm">
        <FeatureListItem
          label={
            <FormattedMessage
              id="subscriptions.features.eventsStorage"
              values={{ size: getFormattedValue(license.event_storage_gb) }}
            />
          }
          isIncluded
        />

        <FeatureListItem
          label={
            <FormattedMessage
              id="subscriptions.features.datasetStorage"
              values={{ size: getFormattedValue(license.dataset_storage_gb) }}
            />
          }
          isIncluded
        />

        <FeatureListItem
          label={
            <FormattedMessage
              id="subscriptions.features.eventsRetention"
              values={{
                months: getFormattedValue(license.event_retention_months)
              }}
            />
          }
          isIncluded
        />

        <FeatureListItem
          label={
            <FormattedMessage id="subscriptions.features.labelingService" />
          }
          isIncluded={license.labeling_service}
        />
      </List>
    </SimpleGrid>
  )
}
